import { Business, ChildCare, Email, Person } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import React from "react";
import type { Campus, Child } from "../../types";
import type { Order } from "../../types/api";

interface CustomerInfoCardProps {
  order: Order;
  child: Child | null;
  campus: Campus | null;
  loading?: boolean;
}

const CustomerInfoCard: React.FC<CustomerInfoCardProps> = ({
  order,
  child,
  campus,
  loading = false,
}) => {
  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Person sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Customer Information
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Parent Information */}
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              PARENT DETAILS
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: "primary.light", width: 48, height: 48 }}>
                <Person />
              </Avatar>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {order.parentInfo?.name || "Unknown Customer"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Email sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary">
                    {order.parentInfo?.email || order.parentId}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>

          {/* Child Information */}
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              CHILD DETAILS
            </Typography>
            {loading ? (
              <Box>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
            ) : child ? (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Avatar
                  sx={{ bgcolor: "secondary.light", width: 48, height: 48 }}
                >
                  <ChildCare />
                </Avatar>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {child.name || (child as any).childNameFirstLast}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Child ID: {child.childID}
                  </Typography>
                  {child.dob && (
                    <Typography variant="caption" color="text.secondary">
                      DOB: {new Date(child.dob).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Child information not available
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider />
          </Grid>

          {/* Campus Information */}
          <Grid size={{ xs: 12 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              CAMPUS DETAILS
            </Typography>
            {loading ? (
              <Box>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="80%" height={24} />
              </Box>
            ) : campus ? (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Avatar
                  sx={{ bgcolor: "success.light", width: 48, height: 48 }}
                >
                  <Business />
                </Avatar>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {campus.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {campus.address}
                  </Typography>
                  {campus.region && (
                    <Typography variant="caption" color="text.secondary">
                      Region: {campus.region}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Campus information not available
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default CustomerInfoCard;
